'use client'

import { useLabels } from 'labels/hooks/useLabels/useLabels'
import replacePlaceholders from 'labels/lib/replacePlaceholders'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import { OrderLine } from 'my-purchases/components/PurchaseCard/components/OrderLine/OrderLine'
import type { Order } from 'my-purchases/types/purchaseList'
import { getStatus } from 'my-purchases/utils/getStatus'
import { ClientLink } from 'utils/link/client'

import styles from 'my-purchases/components/PurchaseCard/components/CarouselHeader/CarouselHeader.module.scss'

interface MultiHeaderProps {
	readonly orders: Order[]
	readonly link: string
	readonly dataTestId: string
	readonly isActive: boolean
	readonly onClick: () => void
}

export function MultiHeader({
	link,
	orders,
	isActive,
	dataTestId,
	onClick,
}: MultiHeaderProps) {
	const { t } = useLabels()
	const {
		country: { locale },
	} = useMasterData()

	const orderStatuses = orders.map((order) =>
		getStatus({ order, locale, isActive })
	)

	const multiOrderAriaLabel = () => {
		const deliveriesText = replacePlaceholders(
			t('accessibility.myPurchases.orderCard.multi.text'),
			{ 1: orders.length }
		)

		const ordersText = orders.map((order, index) => {
			const { title, subtitle, alert, placeholders } = orderStatuses[index]

			const descriptionLabel = placeholders
				? replacePlaceholders(t(alert ?? subtitle), placeholders)
				: t(alert ?? subtitle)

			return replacePlaceholders(
				t('accessibility.myPurchases.orderCard.multi.description.text'),
				{
					1: index + 1,
					2: `${t(title)}, ${descriptionLabel}`,
					3: order.lines?.length ?? 0,
				}
			)
		})

		return `${deliveriesText}. ${ordersText}`
	}

	const ariaLabel = multiOrderAriaLabel()

	return (
		<ClientLink
			href={link}
			className={`${styles.link} ${styles.multi}`}
			onClick={onClick}
			linkProps={{ 'aria-label': ariaLabel }}
			data-testid={dataTestId}
		>
			{orders.map((order, index) => {
				const { title, subtitle, alert, placeholders } = orderStatuses[index]

				return (
					<div className={styles.wrapper} key={order.id}>
						<OrderLine
							status={title}
							description={subtitle}
							alert={alert}
							placeholders={placeholders}
							deliveryIndex={index + 1}
							direction='row'
							dataTestId={dataTestId}
						/>
					</div>
				)
			})}
		</ClientLink>
	)
}
